import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "@/lib/apiClient";
import {
  getDokument,
  replaceDokumentPositionen,
  updateDokument,
  type DokumentDetail,
} from "@/lib/dokumente";
import { patchOrderReferenzFields } from "@/lib/projectOverview";
import { snapshotDocFields } from "@/pages/dokumente/documentEditorSnapshot";
import { adoptReplacedPositionIds } from "@/lib/dokumentBilling";
import { toApiPosition, type EditorPosition } from "@/pages/dokumente/documentPositionModel";

export type DokumentSaveState = "idle" | "pending" | "saving" | "saved" | "error";

export type DokumentAutoSaveResult = {
  /** True wenn während des Speicherns lokal weiter editiert wurde – Positionen nicht überschreiben. */
  stale: boolean;
  /** localIds in der Reihenfolge des gespeicherten Positions-Payloads. */
  savedLocalIds?: string[];
  /** Positions-IDs zum Speicherzeitpunkt, gleiche Reihenfolge wie die Server-Antwort. */
  savedPositionIds?: Array<number | null>;
};

interface UseDokumentAutoSaveOptions {
  doc: DokumentDetail | null;
  positions: EditorPosition[];
  dirty: boolean;
  editable: boolean;
  debounceMs?: number;
  onSaved: (updated: DokumentDetail, result?: DokumentAutoSaveResult) => void;
}

/**
 * Versuche innerhalb eines Save-Durchlaufs. Bewusst klein: ein Durchlauf soll
 * schnell mit Ergebnis enden (UI nicht minutenlang „Speichert …“ zeigen) –
 * danach übernimmt der Hintergrund-Retry (`AUTO_RETRY_DELAYS_MS`).
 */
const SAVE_MAX_ATTEMPTS = 2;
const SAVE_RETRY_BASE_MS = 1_500;
/**
 * Automatische Wiederholung nach fehlgeschlagenem Durchlauf (z. B. Server kurz
 * nicht erreichbar). Backoff, gedeckelt auf den letzten Wert; zusätzlich sofortiger
 * Versuch bei `online`/Fokus-Wechsel. Eingaben bleiben lokal erhalten.
 */
export const AUTO_RETRY_DELAYS_MS = [3_000, 5_000, 10_000, 20_000, 30_000] as const;
/**
 * Obergrenze für `flushSave`-Durchläufe (laufenden Save abwarten → nachspeichern).
 * Verhindert Endlosschleifen, falls parallel dauerhaft weiter editiert wird.
 */
const FLUSH_MAX_ROUNDS = 5;

export function autoRetryDelayMs(attempt: number): number {
  const idx = Math.max(0, Math.min(attempt, AUTO_RETRY_DELAYS_MS.length - 1));
  return AUTO_RETRY_DELAYS_MS[idx] ?? AUTO_RETRY_DELAYS_MS[0];
}

function parseSavedAt(value?: string | null): Date | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function isRetryableSaveError(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  if (err.status === 0) return true;
  if (err.status === 408 || err.status === 409) return true;
  if (err.status === 425 || err.status === 429) return true;
  if (err.status === 502 || err.status === 503 || err.status === 504) return true;
  return false;
}

/**
 * Hintergrund-Retry nur bei Infrastruktur-Problemen (Netz, Timeout, Überlast, 5xx,
 * Versionskonflikt). Fachliche Fehler (4xx wie 422) ändern sich ohne neue Eingabe nicht.
 */
export function shouldAutoRetryAfter(err: unknown): boolean {
  if (!(err instanceof ApiError)) return true;
  if (isRetryableSaveError(err)) return true;
  return err.status >= 500;
}

export function dokumentAutosaveParts(
  lastHeaderSnap: string | null,
  lastPositionsSnap: string | null,
  headerSnap: string,
  positionsSnap: string,
  addrChanged = false,
): { header: boolean; positions: boolean } {
  return {
    header: lastHeaderSnap == null || lastHeaderSnap !== headerSnap || addrChanged,
    positions: lastPositionsSnap == null || lastPositionsSnap !== positionsSnap,
  };
}

function formatSaveError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 0) {
      return "Server nicht erreichbar oder antwortet nicht. Eingaben bleiben erhalten – es wird automatisch erneut gespeichert.";
    }
    if (err.status === 409) {
      return "Dokument wurde zwischenzeitlich geändert. Eingaben bleiben erhalten – es wird automatisch erneut gespeichert.";
    }
    if (err.status === 502 || err.status === 503 || err.status === 504) {
      return "Server kurz überlastet. Eingaben bleiben erhalten – es wird automatisch erneut gespeichert.";
    }
    return err.message;
  }
  return "Automatisches Speichern fehlgeschlagen. Eingaben bleiben erhalten – es wird automatisch erneut versucht.";
}

export function useDokumentAutoSave({
  doc,
  positions,
  dirty,
  editable,
  debounceMs = 700,
  onSaved,
}: UseDokumentAutoSaveOptions) {
  const [saveState, setSaveState] = useState<DokumentSaveState>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  /** Zeitpunkt des nächsten automatischen Versuchs nach Fehler (für die Statusanzeige). */
  const [nextRetryAt, setNextRetryAt] = useState<Date | null>(null);
  const timerRef = useRef<number | null>(null);
  const retryTimerRef = useRef<number | null>(null);
  /** Anzahl der bisher fehlgeschlagenen Hintergrund-Versuche in Folge (Backoff-Stufe). */
  const autoRetryAttemptRef = useRef(0);
  /** `performSave` wird nach `runSave` definiert – Ref bricht den Zyklus. */
  const performSaveRef = useRef<() => Promise<void>>(async () => undefined);
  const savingRef = useRef(false);
  const queuedRef = useRef(false);
  /** Laufender Save – `flushSave` wartet darauf statt ihn nur zu „queuen“. */
  const inflightRef = useRef<Promise<boolean> | null>(null);
  const docRef = useRef(doc);
  const positionsRef = useRef(positions);
  const dirtyRef = useRef(dirty);
  /** Steigt bei jeder lokalen Positions-/Doc-Änderung – erkennt veraltete Save-Antworten. */
  const editGenerationRef = useRef(0);
  /**
   * Nach erfolgreichem Kopf-PATCH (version++), bevor Positions-PUT durch ist.
   * Verhindert 409 beim Retry, ohne React-State mitten im Save anzufassen.
   */
  const versionOverrideRef = useRef<{ version: number; updated_at: string } | null>(null);
  /** Zuletzt erfolgreich gespeicherte Rechnungsadresse – nur bei Wechsel mitsenden. */
  const lastSavedRechnungsadresseIdRef = useRef<number | null | undefined>(undefined);
  const lastSavedReferenzRef = useRef<{
    bestellnummer: string;
    kundenreferenz: string;
    leitweg_id: string;
  } | null>(null);
  /** Nach dem ersten Save nur noch den geänderten Teil schicken (Kopf vs. Positionen). */
  const lastSavedHeaderSnapRef = useRef<string | null>(null);
  const lastSavedPositionsSnapRef = useRef<string | null>(null);

  // Doc-Ref aktuell halten, Override (Zwischen-Version) nicht verlieren.
  docRef.current =
    doc && versionOverrideRef.current
      ? {
          ...doc,
          version: versionOverrideRef.current.version,
          updated_at: versionOverrideRef.current.updated_at,
        }
      : doc;
  positionsRef.current = positions;
  dirtyRef.current = dirty;

  // Jede lokale Änderung (Kopfdaten und Positionen) invalidiert einen laufenden Save –
  // sonst überschreibt die Save-Antwort Eingaben, die während des Requests gemacht wurden
  // (z. B. gelöschter Ansprechpartner taucht wieder auf).
  useEffect(() => {
    editGenerationRef.current += 1;
  }, [doc, positions]);

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current != null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    setNextRetryAt(null);
  }, []);

  useEffect(() => {
    if (!doc) return;
    versionOverrideRef.current = null;
    clearRetryTimer();
    autoRetryAttemptRef.current = 0;
    setLastSavedAt(parseSavedAt(doc.updated_at));
    setSaveError(null);
    setSaveState("idle");
    lastSavedRechnungsadresseIdRef.current = doc.rechnungsadresse_id ?? null;
    lastSavedReferenzRef.current = {
      bestellnummer: (doc.bestellnummer || "").trim(),
      kundenreferenz: (doc.auftrag_kundenreferenz || "").trim(),
      leitweg_id: (doc.leitweg_id || "").trim(),
    };
    lastSavedHeaderSnapRef.current = null;
    lastSavedPositionsSnapRef.current = null;
  }, [doc?.id, clearRetryTimer]);

  const clearSaveTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const applyVersionOverride = useCallback((version: number, updatedAt: string) => {
    versionOverrideRef.current = { version, updated_at: updatedAt };
    if (docRef.current) {
      docRef.current = {
        ...docRef.current,
        version,
        updated_at: updatedAt,
      };
    }
  }, []);

  const refreshDocVersion = useCallback(async (docId: number) => {
    const fresh = await getDokument(docId);
    if (docRef.current?.id !== docId) return;
    applyVersionOverride(fresh.version, fresh.updated_at);
  }, [applyVersionOverride]);

  /**
   * Nach einem fehlgeschlagenen Durchlauf: im Hintergrund erneut versuchen,
   * solange lokal ungespeicherte Änderungen vorliegen. Der Nutzer muss nichts klicken.
   */
  const scheduleAutoRetry = useCallback(() => {
    if (retryTimerRef.current != null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    if (!docRef.current || !editable || !dirtyRef.current) {
      setNextRetryAt(null);
      return;
    }
    const delay = autoRetryDelayMs(autoRetryAttemptRef.current);
    autoRetryAttemptRef.current += 1;
    setNextRetryAt(new Date(Date.now() + delay));
    retryTimerRef.current = window.setTimeout(() => {
      retryTimerRef.current = null;
      setNextRetryAt(null);
      void performSaveRef.current();
    }, delay);
  }, [editable]);

  /** Wartenden Hintergrund-Retry sofort ausführen (z. B. Netz wieder da). */
  const triggerPendingRetryNow = useCallback(() => {
    if (retryTimerRef.current == null) return;
    window.clearTimeout(retryTimerRef.current);
    retryTimerRef.current = null;
    setNextRetryAt(null);
    void performSaveRef.current();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onOnline = () => triggerPendingRetryNow();
    const onVisible = () => {
      if (document.visibilityState === "visible") triggerPendingRetryNow();
    };
    window.addEventListener("online", onOnline);
    window.addEventListener("focus", onOnline);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("focus", onOnline);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [triggerPendingRetryNow]);

  // Beim Unmount keine Timer hinterlassen.
  useEffect(() => {
    return () => {
      if (retryTimerRef.current != null) window.clearTimeout(retryTimerRef.current);
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
    };
  }, []);

  /**
   * Führt genau einen Save-Durchlauf (inkl. Retries) aus.
   * Liefert `true`, wenn der Serverstand danach dem lokalen Stand entspricht
   * (oder nichts zu speichern war), sonst `false`.
   */
  const runSave = useCallback(async (): Promise<boolean> => {
    if (!docRef.current || !editable) return true;

    const generationAtStart = editGenerationRef.current;
    savingRef.current = true;
    if (retryTimerRef.current != null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    setNextRetryAt(null);
    setSaveState("saving");
    setSaveError(null);

    let lastError: unknown = null;
    try {
      for (let attempt = 0; attempt < SAVE_MAX_ATTEMPTS; attempt++) {
        try {
          const docNow = docRef.current;
          const positionsNow = positionsRef.current;
          if (!docNow) return true;

          const currentAddrId = docNow.rechnungsadresse_id ?? null;
          const addrChanged =
            lastSavedRechnungsadresseIdRef.current === undefined ||
            lastSavedRechnungsadresseIdRef.current !== currentAddrId;
          const nextBestellnummer = (docNow.bestellnummer || "").trim();
          const nextKundenreferenz = (docNow.auftrag_kundenreferenz || "").trim();
          const nextLeitwegId = (docNow.leitweg_id || "").trim();
          const prevReferenz = lastSavedReferenzRef.current;
          const referenzChanged =
            !prevReferenz ||
            prevReferenz.bestellnummer !== nextBestellnummer ||
            prevReferenz.kundenreferenz !== nextKundenreferenz ||
            prevReferenz.leitweg_id !== nextLeitwegId;
          const leitwegChanged =
            !!prevReferenz && prevReferenz.leitweg_id !== nextLeitwegId;
          if (docNow.auftrag_id && referenzChanged) {
            await patchOrderReferenzFields(docNow.auftrag_id, {
              bestellnummer: nextBestellnummer || null,
              kundenreferenz: nextKundenreferenz || null,
              ...(leitwegChanged ? { leitweg_id: nextLeitwegId || null } : {}),
            });
            lastSavedReferenzRef.current = {
              bestellnummer: nextBestellnummer,
              kundenreferenz: nextKundenreferenz,
              leitweg_id: nextLeitwegId,
            };
          }

          const headerSnapshot = JSON.stringify(snapshotDocFields(docNow));
          const apiPositions = positionsNow.map((p, idx) => toApiPosition(p, idx, positionsNow));
          const positionsSnapshot = JSON.stringify(apiPositions);
          const { header: headerDirty, positions: positionsDirty } = dokumentAutosaveParts(
            lastSavedHeaderSnapRef.current,
            lastSavedPositionsSnapRef.current,
            headerSnapshot,
            positionsSnapshot,
            addrChanged,
          );

          let headerUpdated = docNow;
          if (headerDirty) {
            headerUpdated = await updateDokument(docNow.id, {
              version: docNow.version,
              betreff: docNow.betreff ?? undefined,
              einleitung: docNow.einleitung ?? undefined,
              schlusstext: docNow.schlusstext ?? undefined,
              interne_notiz: docNow.interne_notiz ?? undefined,
              belegdatum: docNow.belegdatum ?? undefined,
              gueltig_bis: docNow.gueltig_bis ?? undefined,
              leistungsdatum: docNow.leistungsdatum ?? undefined,
              lieferdatum: docNow.lieferdatum ?? undefined,
              leistungszeitraum_von: docNow.leistungszeitraum_von ?? undefined,
              leistungszeitraum_bis: docNow.leistungszeitraum_bis ?? undefined,
              kunde_name: docNow.kunde_name ?? undefined,
              kunde_adresszusatz: docNow.kunde_adresszusatz ?? undefined,
              kunde_strasse: docNow.kunde_strasse ?? undefined,
              kunde_hausnummer: docNow.kunde_hausnummer ?? undefined,
              kunde_plz: docNow.kunde_plz ?? undefined,
              kunde_ort: docNow.kunde_ort ?? undefined,
              kunde_email: docNow.kunde_email ?? undefined,
              kunde_telefon: docNow.kunde_telefon ?? undefined,
              kundennummer: docNow.kundennummer ?? null,
              kunden_id: docNow.kunden_id ?? undefined,
              ansprechpartner_id: docNow.ansprechpartner_id ?? undefined,
              ansprechpartner_name: docNow.ansprechpartner_name ?? undefined,
              ansprechpartner_email: docNow.ansprechpartner_email ?? undefined,
              ansprechpartner_telefon: docNow.ansprechpartner_telefon ?? undefined,
              created_by_name: docNow.created_by_name ?? undefined,
              created_by_email: docNow.created_by_email ?? undefined,
              created_by_telefon: docNow.created_by_telefon ?? undefined,
              created_by_mobil: docNow.created_by_mobil ?? undefined,
              bestellnummer: docNow.bestellnummer ?? undefined,
              leitweg_id: docNow.leitweg_id ?? null,
              referenzdokument_anzeigen: docNow.referenzdokument_anzeigen !== false,
              // EXC2-Antwort gehört zum Entwurf (nicht nur zur Browser-Session).
              ...(docNow.dokument_typ === "angebot"
                ? { exc2_antwort: docNow.exc2_antwort ?? null }
                : {}),
              ...(docNow.dokument_typ === "rechnung"
                ? {
                    // null = normale Rechnung – muss explizit gesendet werden.
                    rechnung_art: docNow.rechnung_art ?? null,
                    reverse_charge_13b: docNow.reverse_charge_13b ?? false,
                  }
                : {}),
              // Unveränderte ID nicht mitsenden – sonst könnte die API den Adress-Snapshot neu anwenden.
              ...(addrChanged ? { rechnungsadresse_id: currentAddrId } : {}),
            });

            // Kopf-Save bump't die Version sofort – sonst 409, wenn der Positions-PUT scheitert.
            applyVersionOverride(headerUpdated.version, headerUpdated.updated_at);
          }

          if (!headerDirty && !positionsDirty) {
            lastSavedHeaderSnapRef.current = headerSnapshot;
            lastSavedPositionsSnapRef.current = positionsSnapshot;
            const stale = generationAtStart !== editGenerationRef.current;
            onSaved(docNow, { stale });
            if (stale) {
              queuedRef.current = true;
              dirtyRef.current = true;
            } else {
              dirtyRef.current = false;
            }
            autoRetryAttemptRef.current = 0;
            setLastSavedAt(parseSavedAt(docNow.updated_at) ?? new Date());
            setSaveError(null);
            setSaveState("saved");
            lastError = null;
            break;
          }

          const updated = positionsDirty
            ? await replaceDokumentPositionen(docNow.id, apiPositions)
            : headerUpdated;
          if (positionsDirty) {
            // Sofort, noch vor dem Follow-up-Save: sonst schickt der nächste
            // Durchlauf die bereits gelöschten Positions-IDs und die Rückfrage
            // verliert die Markierung.
            positionsRef.current = adoptReplacedPositionIds(
              positionsRef.current,
              positionsNow.map((position) => position.localId),
              updated.positionen || [],
            );
          }
          lastSavedHeaderSnapRef.current = headerSnapshot;
          lastSavedPositionsSnapRef.current = positionsSnapshot;
          lastSavedRechnungsadresseIdRef.current = updated.rechnungsadresse_id ?? null;
          const mergedUpdated: DokumentDetail = {
            ...updated,
            bestellnummer: docNow.bestellnummer ?? updated.bestellnummer,
            auftrag_bestellnummer:
              docNow.bestellnummer?.trim() || updated.auftrag_bestellnummer || null,
            auftrag_kundenreferenz:
              docNow.auftrag_kundenreferenz ?? updated.auftrag_kundenreferenz,
            leitweg_id: docNow.leitweg_id ?? updated.leitweg_id,
          };
          versionOverrideRef.current = null;
          const stale = generationAtStart !== editGenerationRef.current;
          onSaved(mergedUpdated, {
            stale,
            ...(positionsDirty
              ? {
                  savedLocalIds: positionsNow.map((position) => position.localId),
                  savedPositionIds: positionsNow.map((position) =>
                    position.id != null && Number.isFinite(Number(position.id))
                      ? Number(position.id)
                      : null,
                  ),
                }
              : {}),
          });
          if (stale) {
            // Version sofort in der Ref aktualisieren, damit der Follow-up-Save keinen 409 bekommt.
            applyVersionOverride(mergedUpdated.version, mergedUpdated.updated_at);
            queuedRef.current = true;
            dirtyRef.current = true;
          } else {
            // Sofort in der Ref markieren – der Render mit `dirty=false` kommt erst später,
            // `flushSave` muss aber direkt nach dem Await wissen, dass alles gespeichert ist.
            dirtyRef.current = false;
          }
          autoRetryAttemptRef.current = 0;
          setLastSavedAt(parseSavedAt(updated.updated_at) ?? new Date());
          setSaveError(null);
          setSaveState("saved");
          lastError = null;
          break;
        } catch (err) {
          lastError = err;
          const canRetry = isRetryableSaveError(err) && attempt < SAVE_MAX_ATTEMPTS - 1;
          if (!canRetry) break;

          if (err instanceof ApiError && err.status === 409 && docRef.current) {
            try {
              await refreshDocVersion(docRef.current.id);
            } catch {
              /* nächster Versuch nutzt ggf. schon gesyncte Version */
            }
          }

          await sleep(SAVE_RETRY_BASE_MS * (attempt + 1));
        }
      }

      if (lastError != null) {
        setSaveError(formatSaveError(lastError));
        setSaveState("error");
        savingRef.current = false;
        if (shouldAutoRetryAfter(lastError)) {
          // Nicht auf einen Klick warten: Änderungen bleiben lokal, Save wird im
          // Hintergrund mit Backoff wiederholt (fängt sich nach kurzer Nichterreichbarkeit).
          scheduleAutoRetry();
        } else {
          // Fachlicher Fehler (z. B. 422): erst die nächste Eingabe löst wieder einen Save aus.
          setNextRetryAt(null);
        }
        return false;
      }
      return true;
    } finally {
      savingRef.current = false;
    }
  }, [applyVersionOverride, editable, onSaved, refreshDocVersion, scheduleAutoRetry]);

  /** Autosave-Pfad: laufenden Save nicht doppeln, sondern Follow-up vormerken. */
  const performSave = useCallback(async (): Promise<void> => {
    if (!docRef.current || !editable) return;
    if (savingRef.current) {
      if (dirtyRef.current) queuedRef.current = true;
      return;
    }

    const run = runSave();
    inflightRef.current = run;
    try {
      await run;
    } finally {
      if (inflightRef.current === run) inflightRef.current = null;
      if (queuedRef.current && dirtyRef.current) {
        queuedRef.current = false;
        void performSave();
      }
    }
  }, [editable, runSave]);
  performSaveRef.current = performSave;

  /**
   * Sofort speichern und auf das Ergebnis warten – für Aktionen, die einen
   * konsistenten Serverstand brauchen (z. B. „Erstellen“, Handy-Upload).
   *
   * Anders als der Autosave-Pfad kehrt dies nicht zurück, solange ein Save läuft,
   * sondern wartet ihn ab und speichert danach ggf. noch offene Änderungen nach.
   * Liefert `false`, wenn der Serverstand nicht dem lokalen Stand entspricht.
   */
  const flushSave = useCallback(async (): Promise<boolean> => {
    clearSaveTimer();
    if (!docRef.current || !editable) return true;

    for (let round = 0; round < FLUSH_MAX_ROUNDS; round++) {
      const inflight = inflightRef.current;
      if (inflight) {
        await inflight;
        // Nach dem Await kann `performSave` bereits einen Follow-up gestartet haben
        // (queued) – nächste Runde prüft `inflightRef` erneut.
        continue;
      }
      if (savingRef.current) {
        // Defensiv: Save läuft, aber kein Promise registriert – kurz warten.
        await sleep(50);
        continue;
      }
      if (!dirtyRef.current) return true;

      const run = runSave();
      inflightRef.current = run;
      let ok = false;
      try {
        ok = await run;
      } finally {
        if (inflightRef.current === run) inflightRef.current = null;
      }
      queuedRef.current = false;
      if (!ok) return false;
    }
    return !dirtyRef.current && !savingRef.current;
  }, [clearSaveTimer, editable, runSave]);

  const retrySave = useCallback(async () => {
    await flushSave();
  }, [flushSave]);

  useEffect(() => {
    if (!editable || !dirty || !doc) return;
    setSaveState("pending");
    clearSaveTimer();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      void performSave();
    }, debounceMs);
    return () => {
      clearSaveTimer();
    };
  }, [doc, positions, dirty, editable, debounceMs, performSave, clearSaveTimer]);

  return { saveState, flushSave, retrySave, lastSavedAt, saveError, nextRetryAt };
}
